import { useEffect, useState } from "react";
import andyHdLogo from "@/assets/andyhdlogo.png";
import styles from "./LoadingOverlay.module.css";

interface LoadingOverlayProps {
  isVisible: boolean;
  message?: string;
  subtitle?: string;
  onDismiss?: () => void;
}

export const LoadingOverlay = ({
  isVisible,
  message,
  subtitle,
  onDismiss,
}: LoadingOverlayProps) => {
  const [showDismiss, setShowDismiss] = useState(false);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | null = null;
    if (isVisible) {
      setShowDismiss(false);
      // If an operation takes longer than 6 seconds, give the user an escape option
      timer = setTimeout(() => {
        setShowDismiss(true);
      }, 6000);
    } else {
      setShowDismiss(false);
    }
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [isVisible]);

  if (!isVisible) return null;

  return (
    <>
      {/* Sleek top-of-viewport streaming progress indicator */}
      <div className={styles.topProgressBar} aria-hidden="true">
        <div className={styles.topProgressTrack} />
      </div>

      {/* Main glassmorphism overlay container */}
      <div className={styles.overlay} role="status" aria-live="polite">
        <div className={styles.loaderCard}>
          {/* Animated Andy Mascot with Orbital Dual Rings */}
          <div className={styles.mascotOrbitalWrapper}>
            <div className={styles.ambientGlow} />
            <svg className={styles.spinnerSvg} viewBox="0 0 76 76" fill="none">
              {/* Subtle background track */}
              <circle
                cx="38"
                cy="38"
                r="33"
                stroke="rgba(0, 168, 107, 0.12)"
                strokeWidth="3.5"
                fill="none"
              />
              {/* Outer glowing orbital gradient ring */}
              <circle
                cx="38"
                cy="38"
                r="33"
                stroke="url(#andyEmeraldGradient)"
                strokeWidth="3.5"
                strokeLinecap="round"
                strokeDasharray="150"
                strokeDashoffset="60"
                fill="none"
                className={styles.outerRing}
              />
              {/* Inner counter-rotating ring */}
              <circle
                cx="38"
                cy="38"
                r="27"
                stroke="rgba(52, 211, 153, 0.35)"
                strokeWidth="1.5"
                strokeDasharray="5 7"
                fill="none"
                className={styles.innerRing}
              />
              <defs>
                <linearGradient id="andyEmeraldGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#00A86B" />
                  <stop offset="50%" stopColor="#10B981" />
                  <stop offset="100%" stopColor="#34D399" />
                </linearGradient>
              </defs>
            </svg>
            <div className={styles.mascotContainer}>
              <img src={andyHdLogo} alt="Andy" className={styles.mascotImg} />
            </div>
          </div>

          {/* Typography & Dynamic Status */}
          <div className={styles.textContent}>
            <p className={styles.loaderTitle}>{message || "Loading..."}</p>
            <div className={styles.subtitleWrapper}>
              <span className={styles.loaderSubtitle}>
                {subtitle || "Andy is getting this ready"}
              </span>
              <span className={styles.bouncingDots}>
                <span className={styles.dot} />
                <span className={styles.dot} />
                <span className={styles.dot} />
              </span>
            </div>
          </div>

          {/* Shimmer progress beam */}
          <div className={styles.progressBarWrapper}>
            <div className={styles.progressBarBeam} />
          </div>

          {/* Interactive dismiss option if request takes unexpectedly long */}
          {showDismiss && (
            <button
              type="button"
              className={styles.dismissBtn}
              onClick={onDismiss}
              title="Close this overlay"
            >
              Taking longer than usual? Click to dismiss
            </button>
          )}
        </div>
      </div>
    </>
  );
};
